import { Button } from '@hologram-dimension/button';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import { Field } from 'components';
import useUserInfo from 'hooks/User/useUserInfo';
import { useCallback, type FC } from 'react';
import type { OnSimsModified, OpenModalCallback } from 'types/common';
import styles from './DeviceDetailsPanel.module.css';

interface DeviceDetailsPanelProps {
  hologramSimId?: HologramSimId;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
}

const DeviceDetailsPanel: FC<DeviceDetailsPanelProps> = ({
  hologramSimId,
  handleModalOpen,
  onSimsModified,
}) => {
  const {
    isLoading: isDeviceLoading,
    error: deviceError,
    deviceDetails,
    isValidating: isDeviceValidating,
  } = useSimDeviceDetails(hologramSimId);
  const {
    isLoading: isSimLoading,
    error: simError,
    simDetails,
    isValidating: isSimValidating,
  } = useSimDetails(hologramSimId);
  const { networkStatus, tunnelable } = simDetails;
  const { claimedDeviceId, imei, imeiTac, manufacturer, model } = deviceDetails;

  const error = deviceError || simError;
  const isLoading = isDeviceLoading || isSimLoading;

  const { releaseFlags } = useUserInfo();

  const hasSpacebridgeTunneling = releaseFlags.has(ReleaseFlag.FEATURE_SPACEBRIDGE_TUNNELING);

  const onToggleTunneling = useCallback(() => {
    handleModalOpen({
      modalName: 'SimInventoryManageTunnelingModal',
      modalProps: {
        sims: new Map([[hologramSimId, claimedDeviceId]]),
        onModalSuccess: onSimsModified,
      },
    });
  }, [handleModalOpen, hologramSimId, claimedDeviceId, onSimsModified]);

  let tunnelableContent;
  const ariaLabel = 'Manage device tunneling'; // for Datadog tracking
  if (tunnelable) {
    tunnelableContent = (
      <Link type="inline" onClick={onToggleTunneling} ariaLabel={ariaLabel}>
        Enabled
      </Link>
    );
  } else if (networkStatus?.startsWith('DEAD')) {
    tunnelableContent = <span className={styles.diminished}>Not enabled</span>;
  } else {
    tunnelableContent = (
      <Link type="inline" onClick={onToggleTunneling} ariaLabel={ariaLabel}>
        Not enabled
      </Link>
    );
  }

  let panelContents;
  if (error) {
    panelContents = (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading device details"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  } else if (isLoading) {
    panelContents = <PanelNotification>Loading...</PanelNotification>;
  } else {
    panelContents = (
      <>
        <div className={styles.panelSection}>
          <div className={styles.panelSectionHeading}>Last known device</div>
          <div className={styles.panelSectionColumns}>
            <div className={styles.panelSectionColumn}>
              <Field label="Device ID" value={claimedDeviceId} valueClassname={styles.numeric} />
            </div>
            <div className={styles.panelSectionColumn}>
              <Field
                label="Last known IMEI"
                value={imei}
                emptyStateText="No known IMEI"
                valueClassname={styles.numeric}
              />
              {imei && (
                <Field
                  label="IMEI TAC"
                  value={imeiTac}
                  emptyStateText="Not available"
                  valueClassname={styles.numeric}
                />
              )}
              {model && manufacturer && (
                <>
                  <Field label="Modem" value={manufacturer} />
                  <Field label="Hardware" value={model} />
                </>
              )}
            </div>
          </div>
        </div>
        {hasSpacebridgeTunneling && (
          <div className={styles.panelSection}>
            <div className={styles.panelSectionHeading}>Device communication</div>
            <div className={styles.panelSectionColumns}>
              <div className={styles.panelSectionColumn}>
                <Field label="Device tunneling" value={tunnelableContent} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Panel
      header={networkStatus?.startsWith('DEAD') ? 'Last known device details' : 'Device details'}
      noContentPadding
      isLoading={isLoading || isDeviceValidating || isSimValidating}
    >
      {panelContents}
    </Panel>
  );
};

export default DeviceDetailsPanel;
